import React, { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Boxes,
  CirclePlay,
  Gauge,
  Handshake,
  KanbanSquare,
  LifeBuoy,
  Menu,
  Truck,
  UsersRound,
  X
} from 'lucide-react';
import api from '../services/api';
import HeroAppMockup from './HeroAppMockup';
import { StockLedgerMock, QualityCertMock, BomBuilderMock } from './ShowcaseMockups';
import './LandingPage.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const heroTrustItems = [
  'Built for any industry',
  'Guided rollout',
  'Role-based access'
];

const industries = [
  'Foam & Polymer',
  'Textiles & Yarn',
  'Food & Beverage',
  'Apparel',
  'Pharma',
  'Chemicals',
  'Metals & Fabrication',
  'Plastics & Rubber',
  'Job Work'
];

// Deep-dive demos (the "show" section). Kept in sync with ShowcaseMockups.
const showcaseItems = [
  {
    key: 'stock',
    eyebrow: 'Stock',
    title: 'Never get caught short on stock',
    body: 'Every inward, outward, hold, and finished-goods movement lands on one running ledger — with alerts before you run out.',
    points: ['Live running balances', 'Automatic reorder alerts', 'Full movement history'],
    Mock: StockLedgerMock
  },
  {
    key: 'quality',
    eyebrow: 'Quality',
    title: 'Ship with a certificate, every time',
    body: 'Record quality parameters at each stage, then generate the test certificate your customer needs — automatically, at dispatch.',
    points: ['Per-stage QC capture', 'Pass / fail on real parameters', 'One-click certificates'],
    Mock: QualityCertMock
  },
  {
    key: 'materials',
    eyebrow: 'Materials',
    title: 'Every product built on clean data',
    body: 'Define materials, their characteristics, and multi-line BOMs so every product is produced and costed from accurate data.',
    points: ['Characteristics per material', 'Multi-line recipes', 'Recipe & non-stock lines'],
    Mock: BomBuilderMock
  }
];

// The remaining capabilities the showcase does not deep-dive.
const featureCards = [
  {
    icon: KanbanSquare,
    title: 'Orders & dispatch',
    description: 'Raise orders, move them across your own workflow stages, and ship in batches with partial dispatch.'
  },
  {
    icon: Gauge,
    title: 'Machines & output',
    description: 'Log machine work, downtime, and maintenance, then see utilisation and OEE across the floor.'
  },
  {
    icon: UsersRound,
    title: 'Access that fits your org',
    description: 'Turn on only the modules a company needs, then control what each role can see and do.'
  }
];

const outcomes = [
  {
    icon: Boxes,
    stat: 'Fewer stockouts',
    text: 'Live balances and reorder alerts catch shortages before they stop a line.'
  },
  {
    icon: Truck,
    stat: 'Faster dispatch',
    text: 'Orders, QC, and certificates move together, so shipments leave without the paperwork scramble.'
  },
  {
    icon: BadgeCheck,
    stat: 'Audit-ready quality',
    text: 'Every check and certificate is recorded against the order, ready when a customer or auditor asks.'
  }
];

const subscriptionCards = [
  {
    icon: Handshake,
    title: 'Hands-on rollout',
    description: 'We help set up the workspace, modules, access, and operating flow around how your company actually runs.'
  },
  {
    icon: CirclePlay,
    title: 'Training and walkthroughs',
    description: 'Start with a guided demo, then use shared recordings and walkthroughs to train owners, admins, and operators.'
  },
  {
    icon: LifeBuoy,
    title: 'Ongoing subscription support',
    description: 'The subscription is built around setup, usage guidance, and operational support — not one-time software delivery.'
  }
];

const onboardingSteps = [
  { n: '1', title: 'Book a demo', text: 'We learn how you produce and show the parts that fit your operation.' },
  { n: '2', title: 'Guided setup', text: 'We configure your materials, BOMs, stages, and access together.' },
  { n: '3', title: 'Train & go live', text: 'Your team is onboarded with walkthroughs and ongoing support.' }
];

const demoAgenda = [
  'Setup & onboarding — modules, roles, and your team',
  'Daily stock & movements — a clean, trusted ledger',
  'Orders, BOM, QC & dispatch — end to end'
];

const faqItems = [
  {
    question: 'Which industries is it built for?',
    answer: 'Any material-based maker — foam, textiles, food, pharma, apparel, chemicals, metals, and more. Materials, characteristics, BOMs, and workflow stages are configured to match how your sector actually produces.'
  },
  {
    question: 'Can I book a demo before deciding anything?',
    answer: 'Yes. The main entry path is a booked demo, where we walk through the product and discuss what parts fit your operation.'
  },
  {
    question: 'Will you help us with setup and training?',
    answer: 'Yes. The subscription is meant to include rollout help, guided onboarding, and training support, not just software access.'
  },
  {
    question: 'Can different companies see different tabs and features?',
    answer: 'Yes. Each company can have a different workspace, and user roles inside that company can be restricted separately.'
  },
  {
    question: 'Is our company data secure?',
    answer: 'Each company’s data is isolated, access is role-based, and our terms include a dedicated data-security and liability agreement aligned with India’s DPDP Act. You can export your data at any time.'
  },
  {
    question: 'How is it priced?',
    answer: 'A monthly subscription that includes a set number of users, with a simple per-user add-on beyond that, plus a one-time setup. Book a demo for a quote tailored to your team.'
  }
];

const initialFormState = {
  name: '',
  company_name: '',
  email: '',
  phone: '',
  industry: '',
  team_size: '',
  message: ''
};

const SUPPORT_EMAIL = 'support@theinventorystudio.com';

function LandingPage() {
  const [openFaqKey, setOpenFaqKey] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const navItems = [
    { label: 'Industries', href: '#industries' },
    { label: 'Product', href: '#product' },
    { label: 'FAQ', href: '#faq' }
  ];

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const sessionKey = `landing_page_viewed:${window.location.pathname}`;
    if (sessionStorage.getItem(sessionKey) === 'true') return;
    sessionStorage.setItem(sessionKey, 'true');

    const referrerHost = (() => {
      try {
        if (!document.referrer) return 'direct';
        return new URL(document.referrer).hostname || 'direct';
      } catch (_err) {
        return 'direct';
      }
    })();
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || '';

    api.trackWebsiteEvent({
      event_type: 'page_view',
      source_path: window.location.pathname || '/',
      referrer_host: referrerHost,
      utm_source: utmSource
    }).catch(() => {});
  }, []);

  // Scroll-reveal: fade each section in as it enters the viewport (hero stays visible).
  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return undefined;
    const viewportH = window.innerHeight || 800;
    // Only hide sections that start below the fold — the visible page is never
    // hidden, so content can't get stuck invisible even if nothing fires.
    const els = Array.from(document.querySelectorAll('.landing-main > section:not(.landing-hero)'))
      .filter((el) => el.getBoundingClientRect().top > viewportH * 0.9);
    els.forEach((el) => el.classList.add('reveal'));
    const reveal = (el) => el.classList.add('is-visible');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    // Failsafe: never leave a section permanently hidden if the observer
    // doesn't fire (e.g. loaded in a background tab).
    const failsafe = setTimeout(() => els.forEach(reveal), 2500);
    return () => { io.disconnect(); clearTimeout(failsafe); };
  }, []);

  const trackDemoClick = (placement) => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || '';
    api.trackWebsiteEvent({
      event_type: 'demo_cta_click',
      source_path: window.location.pathname || '/',
      referrer_host: placement,
      utm_source: utmSource,
      metadata: { placement }
    }).catch(() => {});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError('');
    setFormSuccess('');
    try {
      await api.createContactLead({
        ...form,
        source_path: window.location.pathname || '/'
      });
      setForm(initialFormState);
      setFormSuccess('Thanks. Your demo request is in. We will reach out shortly.');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <header className="landing-topbar">
        <div className="landing-brand">
          <span className="landing-brand-mark" aria-hidden="true">
            <img src="/logo192.png" alt="" />
          </span>
          <div>
            <strong>Inventory Studio</strong>
            <small>Inventory & production software</small>
          </div>
        </div>
        <nav className="landing-nav" aria-label="Landing page sections">
          {navItems.map((item) => (
            <a
              key={item.href}
              className="landing-nav-link"
              href={item.href}
              onClick={() => setMobileNavOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="landing-topbar-actions">
          <a className="topbar-link" href="#book-demo" onClick={() => trackDemoClick('topbar')}>
            Book demo
          </a>
          <a className="topbar-button" href="/login">Sign in</a>
        </div>
        <button
          type="button"
          className="landing-mobile-nav-toggle"
          onClick={() => setMobileNavOpen((previous) => !previous)}
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
        >
          {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </header>

      {mobileNavOpen && (
        <div className="landing-mobile-nav" role="dialog" aria-label="Navigation menu">
          <div className="landing-mobile-nav-links">
            {navItems.map((item) => (
              <a
                key={`mobile-${item.href}`}
                href={item.href}
                onClick={() => setMobileNavOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#book-demo"
              onClick={() => {
                trackDemoClick('mobile_nav_demo');
                setMobileNavOpen(false);
              }}
            >
              Book Demo
            </a>
            <a href="/login" onClick={() => setMobileNavOpen(false)}>
              Sign In
            </a>
          </div>
        </div>
      )}

      <main className="landing-main">
        {/* 1 — HERO (hook) */}
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="hero-kicker">Inventory & production software for manufacturers</p>
            <h1>
              Manage <span className="hero-highlight">stock, orders, and production</span> from one workspace built around your industry.
            </h1>
            <p className="hero-subtitle">
              From raw materials and BOMs to orders, the production floor, quality checks, and dispatch —
              whether you make foam, yarn, food, garments, or medicine.
            </p>
            <div className="hero-actions">
              <a className="hero-primary" href="#book-demo" onClick={() => trackDemoClick('hero_primary')}>
                Book a demo
              </a>
              <a className="hero-secondary" href="/login">Existing customer sign in</a>
            </div>
            <div className="hero-trust-row" aria-label="Inventory Studio highlights">
              {heroTrustItems.map((item) => (
                <span key={item} className="hero-trust-pill">{item}</span>
              ))}
            </div>
            <p className="hero-footnote">Guided rollout and support, set up around how your company actually runs.</p>
          </div>
          <div className="landing-hero-media">
            <HeroAppMockup />
          </div>
        </section>

        {/* 2 — INDUSTRIES (relevance band) */}
        <section id="industries" className="landing-section landing-industries">
          <p className="industries-lead">
            <strong>One platform, tuned to your industry.</strong> Materials, characteristics, BOMs, and workflow
            stages are configured to match how your sector actually produces.
          </p>
          <div className="industry-strip">
            {industries.map((name) => (
              <span key={name} className="industry-chip">{name}</span>
            ))}
            <span className="industry-chip industry-chip-more">+ your sector</span>
          </div>
        </section>

        {/* 3 — SHOWCASE (show) */}
        <section id="product" className="landing-section landing-showcase">
          <div className="section-heading">
            <span className="section-eyebrow">Product tour</span>
            <h2>See it in motion</h2>
            <p>A closer look at the everyday flows your team will live in.</p>
          </div>
          <div className="showcase-rows">
            {showcaseItems.map(({ key, eyebrow, title, body, points, Mock }) => (
              <div className="showcase-row" key={key}>
                <div className="showcase-copy">
                  <span className="showcase-eyebrow">{eyebrow}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <ul className="showcase-points">
                    {points.map((point) => <li key={point}>{point}</li>)}
                  </ul>
                  <a href="#book-demo" onClick={() => trackDemoClick(`showcase:${key}`)}>
                    Book a demo →
                  </a>
                </div>
                <div className="showcase-visual"><Mock /></div>
              </div>
            ))}
          </div>
        </section>

        {/* 4 — CAPABILITIES (explain the rest) */}
        <section className="landing-section">
          <div className="section-heading">
            <span className="section-eyebrow">Capabilities</span>
            <h2>And everything around them</h2>
            <p>Orders, machines, and access — the rest of your operation, in the same system.</p>
          </div>
          <div className="feature-grid">
            {featureCards.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="feature-card">
                  <div className="landing-card-header">
                    <div className="landing-card-icon"><Icon size={18} /></div>
                    <h3>{feature.title}</h3>
                  </div>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* 5 — OUTCOMES (prove) + mid-page CTA */}
        <section className="landing-outcomes">
          <div className="section-heading">
            <span className="section-eyebrow">Outcomes</span>
            <h2>What changes when it’s all in one place</h2>
          </div>
          <div className="outcomes-grid">
            {outcomes.map((o) => {
              const Icon = o.icon;
              return (
                <div className="outcome" key={o.stat}>
                  <div className="outcome-icon"><Icon size={20} /></div>
                  <strong>{o.stat}</strong>
                  <p>{o.text}</p>
                </div>
              );
            })}
          </div>
          <div className="outcomes-cta">
            <a className="hero-primary" href="#book-demo" onClick={() => trackDemoClick('outcomes_cta')}>
              See it on your own workflow
            </a>
          </div>
        </section>

        {/* 6 — ROLLOUT & SUPPORT (reassure) */}
        <section className="landing-section landing-subscription">
          <div className="section-heading">
            <span className="section-eyebrow">Getting started</span>
            <h2>Rollout and support, included</h2>
            <p>Built around setup, training, and ongoing operational help instead of a one-size-fits-all handoff.</p>
          </div>
          <div className="subscription-grid">
            {subscriptionCards.map((card) => {
              const Icon = card.icon;
              return (
                <article key={card.title} className="subscription-card">
                  <div className="landing-card-header">
                    <div className="landing-card-icon"><Icon size={18} /></div>
                    <h3>{card.title}</h3>
                  </div>
                  <p>{card.description}</p>
                </article>
              );
            })}
          </div>
          <ol className="onboarding-steps">
            {onboardingSteps.map((step) => (
              <li key={step.n}>
                <span className="onboarding-num">{step.n}</span>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* 7 — FAQ (objections) */}
        <section id="faq" className="landing-section landing-faq">
          <div className="section-heading">
            <span className="section-eyebrow">FAQ</span>
            <h2>Quick questions</h2>
            <p>Short answers before you book the demo.</p>
          </div>
          <div className="faq-list">
            {faqItems.map((item) => (
              <div key={item.question} className="faq-item">
                <button
                  type="button"
                  className="faq-question"
                  onClick={() => setOpenFaqKey((prev) => (prev === item.question ? null : item.question))}
                  aria-expanded={openFaqKey === item.question}
                >
                  <span>{item.question}</span>
                  <span className="faq-toggle" aria-hidden="true">{openFaqKey === item.question ? '−' : '+'}</span>
                </button>
                {openFaqKey === item.question && <p>{item.answer}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* 8 — BOOK A DEMO (convert) */}
        <section id="book-demo" className="landing-section landing-contact">
          <div className="contact-copy">
            <p className="hero-kicker">Book a demo</p>
            <h2>Tell us what you make, and we’ll tailor the demo to your operation.</h2>
            <p>
              Share a bit about your products, your team, and what is slowing you down today. We will walk you through
              the parts of the platform that fit your operation — not a generic pitch.
            </p>
            <div className="demo-agenda">
              <span className="demo-agenda-label">What your demo covers</span>
              <ul>
                {demoAgenda.map((topic) => <li key={topic}>{topic}</li>)}
              </ul>
            </div>
            <p className="contact-support-line">
              Prefer email? Reach us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
            <div className="contact-note-list">
              <span>Friendly intro call</span>
              <span>Product walkthrough</span>
              <span>Workflow-fit discussion</span>
            </div>
          </div>
          <form className="contact-form-card" onSubmit={handleSubmit}>
            <div className="contact-form-grid">
              <label>
                Name
                <input value={form.name} onChange={(e) => updateForm('name', e.target.value)} required />
              </label>
              <label>
                Company
                <input value={form.company_name} onChange={(e) => updateForm('company_name', e.target.value)} required />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} required />
              </label>
              <label>
                Phone
                <input value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
              </label>
              <label>
                Industry
                <input value={form.industry} onChange={(e) => updateForm('industry', e.target.value)} placeholder="Foam, textiles, food, pharma..." />
              </label>
              <label>
                Team size
                <input value={form.team_size} onChange={(e) => updateForm('team_size', e.target.value)} placeholder="Example: 10-25 users" />
              </label>
              <label className="contact-form-wide">
                What should we understand before the demo?
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => updateForm('message', e.target.value)}
                  placeholder="Tell us what you want to track, who will use the system, or what is broken today."
                  required
                />
              </label>
            </div>
            {formError && <div className="contact-form-feedback is-error">{formError}</div>}
            {formSuccess && <div className="contact-form-feedback is-success">{formSuccess}</div>}
            <div className="contact-form-actions">
              <button type="submit" className="hero-primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Request demo'}
              </button>
              <span className="helper-text">We will review your request and follow up with the right next step for your team.</span>
            </div>
          </form>
        </section>
      </main>
      <footer className="landing-footer">
        <span>
          Support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </span>
        <a href="/login">Sign in</a>
      </footer>
    </div>
  );
}

export default LandingPage;
